class SavingsCalculator extends HTMLElement {
  constructor() {
    super();

    this.flightsInput = this.querySelector('[data-flights-input]');
    this.feeInput = this.querySelector('[data-fee-input]');
    this.addButton = this.querySelector('[data-add-to-cart]');
    this.errorMessage = this.querySelector('[data-error-message]');
    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  connectedCallback() {
    if (!this.flightsInput || !this.feeInput) return;

    this.flightsInput.addEventListener('input', this.update.bind(this));
    this.feeInput.addEventListener('input', this.update.bind(this));
    this.addButton?.addEventListener('click', this.addToCart.bind(this));
    this.update();
  }

  get goCostCents() {
    const productPrice = Number(this.dataset.productPriceCents);
    const fallbackPrice = Number(this.dataset.fallbackPriceCents);

    return Number.isFinite(productPrice) && productPrice > 0 ? productPrice : fallbackPrice;
  }

  get goCost() {
    return Math.round(this.goCostCents / 100);
  }

  get variantId() {
    return Number(this.dataset.variantId);
  }

  get hasAvailableVariant() {
    return this.dataset.productAvailable === 'true' && Number.isFinite(this.variantId) && this.variantId > 0;
  }

  formatMoney(value) {
    const roundedValue = Math.round(value);
    const moneyFormat = this.dataset.moneyFormat || '€{{amount_no_decimals}}';

    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(roundedValue * 100, moneyFormat);
    }

    return `€${roundedValue}`;
  }

  renderTemplate(template, values) {
    return template.replace(/\{([a-z_]+)\}/g, (match, key) => values[key] ?? match);
  }

  setText(selector, value) {
    const element = this.querySelector(selector);
    if (element) element.textContent = value;
  }

  setRangeProgress(input) {
    const min = Number(input.min);
    const max = Number(input.max);
    const value = Number(input.value);
    const progress = ((value - min) / (max - min)) * 100;
    input.style.setProperty('--savings-calculator-range-progress', `${progress}%`);
  }

  update() {
    const flights = Number(this.flightsInput.value);
    const fee = Number(this.feeInput.value);
    const currentFees = flights * fee;
    const goCost = this.goCost;
    const saving = currentFees - goCost;
    const formattedCurrentFees = this.formatMoney(currentFees);
    const formattedGoCost = this.formatMoney(goCost);
    const formattedSaving = this.formatMoney(Math.abs(saving));
    const signedSaving = `${saving >= 0 ? '+' : '-'}${formattedSaving}`;
    const values = {
      flights,
      fee: this.formatMoney(fee),
      current_fees: formattedCurrentFees,
      go_cost: formattedGoCost,
      saving: formattedSaving,
      signed_saving: signedSaving,
    };

    this.setRangeProgress(this.flightsInput);
    this.setRangeProgress(this.feeInput);
    this.setText('[data-flights-value]', flights);
    this.setText('[data-fee-value]', this.formatMoney(fee));
    this.setText('[data-fee-min-label]', this.formatMoney(Number(this.feeInput.min)));
    this.setText('[data-fee-max-label]', this.formatMoney(Number(this.feeInput.max)));
    this.setText('[data-current-fees-total]', formattedCurrentFees);
    this.setText('[data-go-cost-total]', formattedGoCost);
    this.setText('[data-fee-equation]', `${flights} flights x ${this.formatMoney(fee)}`);
    this.setText('[data-year-fee]', `-${formattedCurrentFees}/yr`);
    this.setText('[data-card-go-cost]', formattedGoCost);
    this.setText('[data-net-saving]', signedSaving);

    const maxBarValue = Math.max(currentFees, goCost, 1);
    const feeBar = this.querySelector('[data-fee-bar]');
    const goBar = this.querySelector('[data-go-bar]');

    if (feeBar) feeBar.style.width = `${(currentFees / maxBarValue) * 100}%`;
    if (goBar) goBar.style.width = `${(goCost / maxBarValue) * 100}%`;

    const blackLabel = saving >= 0
      ? this.renderTemplate(this.dataset.blackLabelTemplate || '', values)
      : this.renderTemplate(this.dataset.notEnoughLabelTemplate || '', values);
    const greenLabel = this.renderTemplate(this.dataset.greenLabelTemplate || '', values);
    const notEnoughMessage = this.querySelector('[data-not-enough-message]');

    this.setText('[data-product-link]', blackLabel);
    this.setText('[data-add-to-cart-text]', greenLabel);

    if (notEnoughMessage) {
      notEnoughMessage.textContent = this.dataset.notEnoughText || '';
      notEnoughMessage.toggleAttribute('hidden', saving >= 0);
    }

    if (this.addButton) {
      this.addButton.disabled = !this.hasAvailableVariant || this.addButton.classList.contains('loading');
    }
  }

  showError(message) {
    if (!this.errorMessage) return;
    this.errorMessage.textContent = message || '';
    this.errorMessage.toggleAttribute('hidden', !message);
  }

  addToCart(event) {
    event.preventDefault();
    if (!this.hasAvailableVariant || !this.addButton || this.addButton.disabled) return;

    this.showError('');
    this.addButton.disabled = true;
    this.addButton.classList.add('loading');
    this.addButton.querySelector('.loading__spinner')?.classList.remove('hidden');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.headers['Content-Type'] = 'application/json';

    const body = {
      items: [
        {
          id: this.variantId,
          quantity: 1,
        },
      ],
    };

    if (this.cart) {
      body.sections = this.cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
      this.cart.setActiveElement(this.addButton);
    }

    config.body = JSON.stringify(body);

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'savings-calculator',
            productVariantId: this.variantId,
            errors: response.errors || response.description,
            message: response.message,
          });
          this.showError(response.description || response.message);
          return;
        }

        if (!this.cart) {
          window.location = window.routes.cart_url;
          return;
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'savings-calculator',
          productVariantId: this.variantId,
          cartData: response,
        }).then(() => {
          this.cart.renderContents(response);
        });
      })
      .catch((error) => {
        console.error(error);
        this.showError(window.variantStrings?.cartError || 'Unable to add item to cart.');
      })
      .finally(() => {
        this.addButton.classList.remove('loading');
        this.addButton.querySelector('.loading__spinner')?.classList.add('hidden');
        this.addButton.disabled = !this.hasAvailableVariant;
      });
  }
}

if (!customElements.get('savings-calculator')) {
  customElements.define('savings-calculator', SavingsCalculator);
}
